<template>
  <!-- eslint-disable -->
  <div class="container-fluid">
    <div class="row header">
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
        <p class="dg-header-label">Brand</p>
        <h1 class="h1 dg-header">Typography</h1>
        <p>Typography serves to create hierarchy, demonstrate useful organization and promote readability. Further, consistency in typography creates a cohesive user experience across an array digital products, thus contributing to a unified platform.</p>
      </div>
    </div>

    <div class="row content">
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
        <div class="row">
          <div class="col pb-4">
            <p class="h3 dg-subheader">General</p>
            <p>
              In order to best deliver a familiar experience to users, body fonts are set to the sans-serif system font appropriate to each OS and device.  For headings, we've chosen to use <span class="code">Montserrat</span> which can be <a href="https://fonts.google.com/specimen/Montserrat?selection.family=Montserrat" target="_blank" rel="noopener noreferrer">downloaded</a> from Google Fonts for use in mockups and prototypes.
            </p>

            <div class="row">
              <div class="col-12 col-md-8">
                <table class="table table-sm table-hover dg-varients-modifiers">
                  <thead>
                    <tr>
                      <th scope="col">OS</th>
                      <th scope="col" class="w-50">Font Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><p>Windows</p></td>
                      <td><p>Segoe UI</p></td>
                    </tr>
                    <tr>
                      <td><p>MacOS & iOS</p></th>
                      <td><p>San Francisco</p></td>
                    </tr>
                    <tr>
                      <td><p>Chrome OS & Android</p></td>
                      <td><p>Roboto</p></td>
                    </tr>
                    <tr>
                      <td><p>Ubuntu</p></td>
                      <td><p>Ubuntu</p></td>
                    </tr>
                    <tr>
                      <td><p>OxygenOS</p></td>
                      <td><p>Oxygen</p></td>
                    </tr>
                    <tr>
                      <td><p>Firefox OS</p></td>
                      <td><p>Fira Sans</p></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-10 offset-lg-1 col-xl-8 offset-xl-2">
        <div class="row">
          <div class="col">
            <h2 class="h3 dg-subheader">A few Examples</h2>
            <p class="h1 header-accent-lg mb-4">H1 HEADER TITLE</p>
            <p class="h2 mb-3">H2 HEADER TITLE</p>
            <p class="h3 mb-3">H2 HEADER W/H3 CLASS</p>
            <p><b>Body Copy Bold Black.</b> This is the text style to be used for <b>Emphasis.</b> It is not to be used for entire paragraphs, only when a certain piece of information is <b>important</b> and needs to stand out. </p>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <p class="h3 dg-subheader">Code</p>
            <pre class="code-sample">
              <code class="html">&lt;h1 class="header-accent-lg"&gt;H1 HEADER TITLE&lt;/h1&gt;
&lt;h2&gt;H2 HEADER TITLE&lt;/h2&gt;
&lt;h2 class="h3"&gt;H2 HEADER W/H3 CLASS&lt;/h2&gt;

&lt;p&gt;&lt;b&gt;Body Copy Bold Black.&lt;/b&gt; This is the text style to be used for &lt;b&gt;Emphasis.&lt;/b&gt; 
It is not to be used for entire paragraphs, only when a certain piece of information is &lt;b&gt;important&lt;/b&gt; 
and needs to stand out. &lt;/p&gt;</code>
            </pre>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2 class="h3 dg-subheader">Variants / Modifiers</h2>
            <div class="row">
              <div class="col">
                <h3 class="h4">Classes</h3>
                <p>
                  In order to keep your HTML semantic and meet WCAG standards, headers need to follow a sequential order meaning an <span class="code">H2</span> should follow an <span class="code">H1</span> and so on.  You can apply the different heading sizes as CSS classes in order to follow standards while giving flexibility in design choices.  As an example, you may want an <span class="code">H2</span> to be the size of an <span class="code">H3</span>.  To achieve this, simply attach the <span class="code">H3</span> class to your <span class="code">H2</span> element - <span class="code">&lt;H2 class="h3"&gt;Heading Text&lt;H2&gt;</span>
                </p>
              </div>
            </div>
            <div class="row mt-4">
              <div class="col">
                <h3 class="h4">Heading Accent</h3>
                <p>
                  An accent class can be applied to headings to give a small underline.  The root class is <span class="code">header-accent</span>.  Depending on the size of your heading, the base class may be too big or small.  To solve this we've added <span class="code">header-accent-sm</span> and <span class="code">header-accent-lg</span>.
                </p>
                <p>
                  Users may add <span class="code">header-accent-white</span> class paired with the root accent class to change the color of the underline to white.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row mt-4">
          <div class="col">
            <h2 class="h3 dg-subheader">Accessibility</h2>
            <p>WCAG (Web Content Accessibility Guidelines) ensure that content is accessible by everyone, regardless of disability or user device. To meet these standards, text and interactive elements should have a color contrast ratio of at least 4.5:1. This ensures that viewers who cannot see the full color spectrum are able to read the text.</p>
            <p>In order to ensure a proper color contrast ratio, you can utilize webAIMS' <a href="https://webaim.org/resources/contrastchecker/">Color Contraster Checker</a>.</p>

            <p>
              Headings briefly describe the section it introduces.  Headings represent an outline of the content.
              <ul>
                <li>Use <span class="code">h1</span>-<span class="code">h6</span> to define your section headings, where <span class="code">h1</span> is the highest section level and <span class="code">h6</span> is the lowest</li>
                <li>Avoid skipping heading levels:  Always start with <span class="code">h1</span>, user <span class="code">h2</span> next, and so on.</li>
                <li>Use only one <span class="code">h1</span> per page for the page title.</li>
              </ul>
            </p>
          </div>
        </div>
      </div>
    </div>  
  </div>
  <!-- eslint-enable -->
</template>

<script>
export default {
  name: "Home"
};
</script>

/* esling-disable */
<style lang="sass?outputStyle=expanded">
</style>
/* eslint-enable */



// WEBPACK FOOTER //
// src/components/routes/Typography.vue